import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Autocomplete } from '@react-google-maps/api';
import '../../styles/AddWarehouse.css';
import TopMenu from '../../contexts/TopMenu';
import { API_BASE_URL } from '../../config';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 20.5937, // Default to center of India
  lng: 78.9629
};

function AddWarehouse({ email, onLogout }) {
  const navigate = useNavigate();
  const [marker, setMarker] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: localStorage.getItem('userEmail') || '',
    code: 'WH' + Math.random().toString(36).substring(2, 7).toUpperCase(), // Auto-generate warehouse code
    address: '', // This will store the Google Places selected address
    address_2: '', // This will store the additional address details
    city: '',
    state: '',
    zip: '',
    country: '',
    operationHoursStart: '',
    operationHoursEnd: '',
    pocName: '',
    pocPhone: '',
    pocEmail: '',
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);

  const onPlaceSelected = useCallback((place) => {
    if (!place.geometry || !place.geometry.location) {
      // console.log('Returned place contains no geometry');
      return;
    }

    setSelectedPlace(place);
    const position = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    };
    
    setMarker(position);
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address, // Set the main address from Places API
      latitude: position.lat,
      longitude: position.lng
    }));

    // Update form with place details
    const addressComponents = place.address_components;
    let city = '', state = '', country = '', postalCode = '';

    for (const component of addressComponents) {
      const type = component.types[0];
      switch (type) {
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.long_name;
          break;
        case 'country':
          country = component.long_name;
          break;
        case 'postal_code':
          postalCode = component.long_name;
          break;
        default:
          break;
      }
    }

    setFormData(prev => ({
      ...prev,
      city,
      state,
      country,
      zip: postalCode
    }));
  }, []);

  const onMapClick = useCallback((event) => {
    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    setMarker(position);
    setFormData(prev => ({
      ...prev,
      latitude: position.lat,
      longitude: position.lng
    }));

    // Reverse geocode the clicked location
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const place = results[0];
        const addressComponents = place.address_components;
        let streetNumber = '', route = '', city = '', state = '', country = '', postalCode = '';

        for (const component of addressComponents) {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              streetNumber = component.long_name;
              break;
            case 'route':
              route = component.long_name;
              break;
            case 'locality':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'country':
              country = component.long_name;
              break;
            case 'postal_code':
              postalCode = component.long_name;
              break;
            default:
              break;
          }
        }

        setFormData(prev => ({
          ...prev,
          address: `${streetNumber} ${route}`.trim(),
          city,
          state,
          country,
          zip: postalCode
        }));
      }
    });
  }, []);

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    // Convert HH:MM to HHMM format directly
    const timeValue = value.replace(':', '');
    setFormData(prev => ({ ...prev, [name]: timeValue }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateForm = () => {
    // Check if address was selected from Places API
    if (!selectedPlace) {
      setError('Please select an address from the dropdown suggestions');
      return false;
    }

    // Check required fields
    const requiredFields = [
      'name', 'email', 'address', 'city', 'state', 'zip', 'country',
      'operationHoursStart', 'operationHoursEnd', 'pocName', 'pocPhone', 'pocEmail'
    ];

    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`);
        return false;
      }
    }

    // Validate time format
    const timeRegex = /^([0-1][0-9]|2[0-3])[0-5][0-9]$/;
    if (!timeRegex.test(formData.operationHoursStart) || !timeRegex.test(formData.operationHoursEnd)) {
      setError('Invalid time format');
      return false;
    }

    // Validate coordinates
    if (formData.latitude < -90 || formData.latitude > 90) {
      setError('Invalid latitude');
      return false;
    }
    if (formData.longitude < -180 || formData.longitude > 180) {
      setError('Invalid longitude');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Remove code field as it's not part of the API schema
      const { code, ...submitData } = formData;
      
      // Add required auth_key and user_id
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication information is missing. Please log in again.');
      }
      
      const requestData = {
        ...submitData,
        auth_key: authKey,
        user_id: userId
      };

      const response = await fetch(`${API_BASE_URL}/brands/warehouses/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Failed to add warehouse');
      }

      navigate('/warehouses');
    } catch (error) {
      setError(error.message);
      console.error('Error saving warehouse:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Add Warehouse" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="add-warehouse-form-container">
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleSubmit} className="add-warehouse-form">
            <div className="form-grid">
              <div className="form-section">
                <h2>Basic Information</h2>
                <div className="form-group">
                  <label htmlFor="name">Warehouse Name*</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter warehouse name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Search Location*</label>
                  <Autocomplete
                    onLoad={autocomplete => setSearchBox(autocomplete)}
                    onPlaceChanged={() => {
                      if (searchBox) {
                        const place = searchBox.getPlace();
                        onPlaceSelected(place);
                      }
                    }}
                  >
                    <input
                      type="text"
                      id="address"
                      placeholder="Search and select an address"
                      className="location-search-input"
                      required
                    />
                  </Autocomplete>
                  {!selectedPlace && (
                    <div className="input-hint">Please select an address from the dropdown</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="address_2">Additional Address Details</label>
                  <input
                    type="text"
                    id="address_2"
                    name="address_2"
                    value={formData.address_2}
                    onChange={handleInputChange}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                  />
                </div>

                <div className="map-container">
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={marker || defaultCenter}
                    zoom={marker ? 17 : 5}
                    onClick={onMapClick}
                  >
                    {marker && <Marker position={marker} />}
                  </GoogleMap>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="latitude">Latitude</label>
                    <input
                      type="text"
                      id="latitude"
                      name="latitude"
                      value={formData.latitude}
                      readOnly
                      className="coordinates-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="longitude">Longitude</label>
                    <input
                      type="text"
                      id="longitude"
                      name="longitude"
                      value={formData.longitude}
                      readOnly
                      className="coordinates-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Street Address*</label>
                  <input
                    type="text"
                    id="address-field"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter street address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City*</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State*</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip">ZIP Code*</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter ZIP code"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country*</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter country"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2>Operations Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="operationHoursStart">Opening Time (24h)*</label>
                    <input
                      type="time"
                      id="operationHoursStart"
                      name="operationHoursStart"
                      value={formData.operationHoursStart ? 
                        `${formData.operationHoursStart.slice(0, 2)}:${formData.operationHoursStart.slice(2)}` 
                        : ''}
                      onChange={handleTimeChange}
                      required
                      min="00:00"
                      max="23:59"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="operationHoursEnd">Closing Time (24h)*</label>
                    <input
                      type="time"
                      id="operationHoursEnd"
                      name="operationHoursEnd"
                      value={formData.operationHoursEnd ? 
                        `${formData.operationHoursEnd.slice(0, 2)}:${formData.operationHoursEnd.slice(2)}` 
                        : ''}
                      onChange={handleTimeChange}
                      required
                      min="00:00"
                      max="23:59"
                    />
                  </div>
                </div>

                <h2>Point of Contact</h2>
                <div className="form-group">
                  <label htmlFor="pocName">Contact Name*</label>
                  <input
                    type="text"
                    id="pocName"
                    name="pocName"
                    value={formData.pocName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact person's name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pocPhone">Contact Phone*</label>
                  <input
                    type="tel"
                    id="pocPhone"
                    name="pocPhone"
                    value={formData.pocPhone}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact phone number"
                    pattern="^\+?[1-9]\d{1,14}$"
                    title="Please enter a valid phone number with country code (e.g., +1-555-123-4567)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pocEmail">Contact Email*</label>
                  <input
                    type="email"
                    id="pocEmail"
                    name="pocEmail"
                    value={formData.pocEmail}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact email"
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => navigate('/warehouses')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting} 
                className="action-button"
              >
                {isSubmitting ? 'Adding Warehouse...' : 'Add Warehouse'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddWarehouse; 